/**
 * Split-window side pane (resizable), usable by ANY screen — lists and records alike.
 *
 *  <SplitLayout side={{ open, onOpenChange, title, icon, children, footer, width }}>
 *     …main content (a <Page> with a DataGrid, or a RecordPage body)…
 *  </SplitLayout>
 *
 * Rule of thumb for CRUD: small forms (≤ ~8 fields: edit user, create tenant, assign admin,
 * import CSV, confirmations with options) open in the pane; big forms (new user with student
 * details, questions, leads, courses, exams) open in their own tab via a route.
 *
 *  side.width       initial pane width in % (default 38); minSize default 26
 *  side.storageKey  persists the drag width (default: derived from title)
 *  side.footer      node rendered as a sticky footer (use <PaneFooter> for Save/Cancel)
 *  side.actions     node rendered in the pane header (right of the title)
 *  side.testid      data-testid for the pane
 *
 * <PaneForm onSubmit title…>: wraps children in a <form> and renders a PaneFooter with
 *  Save / Cancel wired to onSubmit / onCancel, so every pane form looks the same.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { Button } from '../ui/button';
import { X, FloppyDisk } from '@phosphor-icons/react';

/** Keep the pane fully inside the viewport: sticky, with its height fitted to the space below its top. */
function useFittedHeight(ref) {
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const scroller = el.closest('main') || document.documentElement;
    const fit = () => {
      const top = el.getBoundingClientRect().top;
      const h = Math.max(320, window.innerHeight - top - 16);
      setHeight(h);
    };
    fit();
    window.addEventListener('resize', fit);
    scroller.addEventListener('scroll', fit, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(scroller);
    return () => {
      window.removeEventListener('resize', fit);
      scroller.removeEventListener('scroll', fit);
      if (ro) ro.disconnect();
    };
  }, [ref]);
  return height;
}

export function SidePaneBody({ side }) {
  const Icon = side.icon;
  const ref = useRef(null);
  const height = useFittedHeight(ref);
  return (
    <aside
      ref={ref}
      style={height ? { height } : undefined}
      className="sticky top-4 flex flex-col bg-white border border-border rounded-lg shadow-sm my-4 mr-4 lg:mr-5 ml-1"
      data-testid={side.testid || 'record-side-pane'}
    >
      <header className="flex items-center gap-2 px-4 h-11 border-b border-border bg-muted/30 rounded-t-lg flex-shrink-0">
        {Icon && <Icon className="w-4 h-4 text-shell-primary" weight="duotone" />}
        <div className="min-w-0 mr-auto">
          <h3 className="text-sm font-semibold truncate leading-tight">{side.title}</h3>
          {side.subtitle && <p className="text-[11px] text-muted-foreground truncate leading-tight">{side.subtitle}</p>}
        </div>
        {side.actions}
        <button
          type="button"
          onClick={() => side.onOpenChange?.(false)}
          className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Close pane"
          data-testid="record-side-close"
        >
          <X className="w-4 h-4" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">{side.children}</div>
      {side.footer && (
        <footer className="flex-shrink-0 border-t border-border bg-muted/20 px-4 py-2.5 rounded-b-lg">{side.footer}</footer>
      )}
    </aside>
  );
}

export default function SplitLayout({ side, children, className = '' }) {
  if (!side?.open) return children;
  const width = side.width || side.defaultSize || 38;
  return (
    <ResizablePanelGroup
      direction="horizontal"
      autoSaveId={side.storageKey || `pane-${String(side.title || 'side').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`flex-1 min-h-0 !h-auto items-stretch ${className}`}
    >
      <ResizablePanel defaultSize={100 - width} minSize={30} className="min-w-0">
        {children}
      </ResizablePanel>
      <ResizableHandle withHandle className="bg-border/80 hover:bg-shell-active/60 w-1.5 mt-4 mb-4 rounded" />
      <ResizablePanel defaultSize={width} minSize={side.minSize || 26} className="min-w-0">
        <SidePaneBody side={side} />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

/** Standard footer: [extra] … [Cancel] [Save] */
export function PaneFooter({ onCancel, onSubmit, submitLabel = 'Save', submitting = false, submitDisabled = false, submitIcon: SubmitIcon = FloppyDisk, cancelLabel = 'Cancel', extra = null, submitVariant = 'default', form }) {
  return (
    <div className="flex items-center gap-2">
      {extra}
      <div className="ml-auto flex items-center gap-2">
        {onCancel && <Button type="button" variant="outline" size="sm" className="h-9" onClick={onCancel}>{cancelLabel}</Button>}
        {(form || onSubmit) && (
          <Button type={form ? 'submit' : 'button'} form={form} variant={submitVariant} size="sm" className="h-9" onClick={form ? undefined : onSubmit} disabled={submitting || submitDisabled} data-testid="pane-submit">
            {SubmitIcon && <SubmitIcon className="w-4 h-4" />}
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Form inside a pane. Renders children inside <form id=…>; the footer's submit button targets
 * that form, so Enter-to-submit and `required` validation keep working.
 * Returns { body, footer } to spread into the `side` object:
 *   const f = paneForm({ id:'tenant-form', onSubmit, onCancel, submitLabel:'Create tenant', children: <…/> });
 *   side={{ open, onOpenChange, title, children: f.body, footer: f.footer }}
 */
export function paneForm({ id, onSubmit, onCancel, submitLabel, submitting, submitDisabled, submitIcon, submitVariant, extra, children }) {
  return {
    body: <form id={id} onSubmit={onSubmit} className="space-y-4">{children}</form>,
    footer: <PaneFooter form={id} onCancel={onCancel} submitLabel={submitLabel} submitting={submitting} submitDisabled={submitDisabled} submitIcon={submitIcon} submitVariant={submitVariant} extra={extra} />,
  };
}
